/**
 * Daftar langkah mengapung untuk pakar yang baru bergabung.
 *
 * Halaman /admin/panduan sifatnya pasif — pakar harus tahu dulu bahwa halaman
 * itu ada. Widget ini yang aktif menyapa: nempel di pojok kanan bawah, dan
 * memberi tahu langkah mana yang belum dikerjakan.
 *
 * ATURAN YANG MENENTUKAN SELURUH RANCANGAN INI:
 * status tiap langkah SELALU DIHITUNG ULANG dari data server, tidak pernah
 * disimpan sebagai daftar. Yang disimpan di tabel users hanya dua hal yang
 * memang tidak bisa disimpulkan dari data (`onboarding_guide_read` dan
 * `onboarding_hidden`). Kalau nanti pakar menghapus kelasnya, langkahnya balik
 * jadi merah dengan sendirinya — mustahil melenceng dari isi admin.
 *
 * SIAPA YANG MELIHATNYA: kedua peran admin.
 *
 * Rancangan awal membatasinya ke `provider_admin` saja, dengan alasan admin
 * platform mengurasi katalog dan bukan mengisinya. Alasan itu tidak berlaku
 * untuk platform yang katalognya masih nol: di keadaan itu tidak ada apa pun
 * untuk dikurasi, dan orang pertama yang masuk justru admin platform. Membiarkan
 * dia menatap dasbor kosong tanpa penunjuk arah adalah kegagalan yang sama
 * persis dengan yang widget ini dibuat untuk mencegah.
 *
 * Isinya mengikuti cakupan pembacanya — API sudah membatasi sendiri lewat
 * adminScope, jadi admin lembaga menghitung isi lembaganya, admin platform
 * menghitung seluruh platform. Langkah terakhir berbeda menurut peran: yang satu
 * MENGAJUKAN kurasi, yang lain MENGURASI.
 *
 * Kalau form/alur di resources.jsx atau CourseEditor.jsx berubah, LANGKAH DI
 * SINI dan teks di Guide.jsx harus diperbarui bersama — kalau tidak, penuntun
 * ini malah menyesatkan.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'
import { useApp } from '../store/AppProvider'
import { Icon, IconButton, ProgressBar, Spinner } from './ui'

/** Jeda minimum antar-perhitungan, supaya pindah halaman cepat tidak memicu
 *  dua panggilan berturut-turut. Membuka panel selalu memaksa hitung ulang. */
const RECHECK_MS = 15000

/** Ambil jumlah total dari envelope `{items, meta}` milik httpx.PaginateReq. */
const totalOf = (res) => {
  if (!res) return 0
  if (typeof res?.meta?.total === 'number') return res.meta.total
  if (Array.isArray(res?.items)) return res.items.length
  if (Array.isArray(res)) return res.length
  return 0
}

const itemsOf = (res) => (Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [])

/**
 * Kelas yang dipakai langkah 4–6 dan jadi tujuan tautannya.
 *
 * Urutan pilihan bukan asal: yang paling butuh perhatian pakar didahulukan.
 * `rejected` ditaruh tinggi karena kelas yang ditolak kurator memang harus
 * dibenahi lebih dulu daripada kelas yang sudah tayang.
 */
const pickTarget = (courses) => {
  const byStatus = (s) => courses.find((c) => c.status === s)
  return byStatus('draft') || byStatus('rejected') || byStatus('pending_review') || courses[0] || null
}

/** Sama persis dengan ReadinessChecklist di CourseEditor.jsx — sengaja, supaya
 *  hitungan di widget dan di kartu "Kesiapan kelas" tidak pernah berbeda. */
const objectivesOf = (c) => (Array.isArray(c?.objectives) ? c.objectives.filter((o) => (o || '').trim()) : [])
const hasDetail = (c) => !!(c?.description && c.description.trim()) && objectivesOf(c).length > 0
const hasCurriculum = (c) => (c?.lesson_count || 0) > 0
const hasPrice = (c) => !!c?.is_free || +(c?.price || 0) > 0

/**
 * Tujuh langkah, urut sesuai alur kerja nyata di Guide.jsx §3 "Urutan kerja".
 * `done` dan `hint` menerima hasil perhitungan, jadi seluruh logikanya terkumpul
 * di satu tempat dan mudah dicocokkan dengan panduan.
 *
 * `title` berupa kunci i18n dan `hint` menerima `t`: daftar ini konstanta modul,
 * jadi ia tidak bisa memanggil useI18n sendiri — yang merender yang menerjemahkan.
 */
const STEPS = [
  {
    key: 'panduan',
    title: 'adm.onb.step.guide',
    icon: 'menu_book',
    to: () => '/admin/panduan',
    done: (d) => d.guideRead,
    hint: (d, t) => (d.guideRead ? t('adm.onb.step.guideDone') : t('adm.onb.step.guideTodo')),
  },
  {
    key: 'pakar',
    title: 'adm.onb.step.instructor',
    icon: 'school',
    to: () => '/admin/instructors',
    done: (d) => d.instructors > 0,
    hint: (d, t) => (d.instructors > 0 ? t('adm.onb.step.instructorDone', { n: d.instructors }) : t('adm.onb.step.instructorTodo')),
  },
  {
    key: 'kelas',
    title: 'adm.onb.step.course',
    icon: 'video_library',
    to: () => '/admin/courses',
    done: (d) => d.courses.length > 0,
    hint: (d, t) => (d.courses.length > 0 ? t('adm.onb.step.courseDone', { n: d.courses.length }) : t('adm.onb.step.courseTodo')),
  },
  {
    key: 'detail',
    title: 'adm.onb.step.detail',
    icon: 'description',
    to: (d) => (d.target ? `/admin/courses/${d.target.id}?tab=detail` : '/admin/courses'),
    done: (d) => hasDetail(d.target),
    hint: (d, t) => {
      if (!d.target) return t('adm.onb.needCourse')
      const n = objectivesOf(d.target).length
      return hasDetail(d.target) ? t('adm.onb.step.detailDone', { n }) : t('adm.ready.detailTodo')
    },
  },
  {
    key: 'silabus',
    title: 'adm.onb.step.syllabus',
    icon: 'list_alt',
    to: (d) => (d.target ? `/admin/courses/${d.target.id}?tab=curriculum` : '/admin/courses'),
    done: (d) => hasCurriculum(d.target),
    hint: (d, t) => {
      if (!d.target) return t('adm.onb.needCourse')
      return hasCurriculum(d.target)
        ? t('adm.cur.count', { m: d.target.module_count || 0, l: d.target.lesson_count || 0 })
        : t('adm.ready.curriculumTodo')
    },
  },
  {
    key: 'harga',
    title: 'adm.onb.step.price',
    icon: 'sell',
    to: (d) => (d.target ? `/admin/courses/${d.target.id}?tab=pricing` : '/admin/courses'),
    done: (d) => hasPrice(d.target),
    hint: (d, t) => {
      if (!d.target) return t('adm.onb.needCourse')
      if (!hasPrice(d.target)) return t('adm.onb.step.priceTodo')
      return d.target.is_free ? t('price.free') : `Rp${(+d.target.price).toLocaleString('id-ID')}`
    },
  },
  {
    key: 'kurasi',
    // Satu-satunya langkah yang berbeda menurut peran. Admin lembaga MENGIRIM
    // kelasnya untuk ditinjau; admin platform yang MENINJAU. Menyuruh admin
    // platform "ajukan kurasi ke platform admin" berarti menyuruhnya mengirim
    // sesuatu kepada dirinya sendiri.
    title: (d) => (d.isPlatform ? 'adm.onb.step.curatePlatform' : 'adm.course.submitReview'),
    icon: 'send',
    to: (d) =>
      d.isPlatform
        ? '/admin/courses?status=pending_review'
        : d.target
          ? `/admin/courses/${d.target.id}?tab=detail`
          : '/admin/courses',
    done: (d) =>
      d.isPlatform
        ? d.courses.some((c) => c.status === 'published')
        : d.courses.some((c) => c.status === 'pending_review' || c.status === 'published'),
    hint: (d, t) => {
      const published = d.courses.filter((c) => c.status === 'published').length
      const waiting = d.courses.filter((c) => c.status === 'pending_review').length
      if (d.isPlatform) {
        if (published) return t('adm.onb.curate.published', { n: published })
        if (waiting) return t('adm.onb.curate.waiting', { n: waiting })
        return t('adm.onb.curate.none')
      }
      if (published) return t('adm.onb.submit.published')
      if (waiting) return t('adm.onb.submit.waiting')
      return t('adm.onb.submit.todo')
    },
  },
]

export function OnboardingWidget() {
  const { t, dir } = useI18n()
  const rtl = dir === 'rtl'
  const { user } = useApp()
  const nav = useNavigate()
  const loc = useLocation()

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [hidden, setHidden] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const isPlatform = user?.role === 'platform_admin'
  const [data, setData] = useState({ guideRead: false, instructors: 0, courses: [], target: null, isPlatform })

  const lastRun = useRef(0)
  const guidePatched = useRef(false)
  const eligible = isPlatform || user?.role === 'provider_admin'

  /** Satu perhitungan = 3 permintaan sejajar. Tiap kegagalan ditelan supaya
   *  satu endpoint bermasalah tidak menjatuhkan seluruh widget — langkahnya
   *  cuma tampil belum-beres, persis pola SetupWidget di workwise-admin. */
  const runChecks = useCallback(async () => {
    if (!eligible) return
    lastRun.current = Date.now()
    setLoading(true)
    const [pref, ins, crs] = await Promise.all([
      api.get('/me/onboarding').catch(() => null),
      api.get('/admin/instructors', { per_page: 1 }).catch(() => null),
      api.get('/admin/courses', { per_page: 100 }).catch(() => null),
    ])
    if (pref?.onboarding_hidden) {
      setHidden(true)
      setLoading(false)
      return
    }
    const courses = itemsOf(crs)
    setData({
      guideRead: !!pref?.onboarding_guide_read,
      instructors: totalOf(ins),
      courses,
      target: pickTarget(courses),
      isPlatform,
    })
    setLoading(false)
  }, [eligible, isPlatform])

  useEffect(() => {
    if (!eligible) return
    if (Date.now() - lastRun.current < RECHECK_MS) return
    runChecks()
  }, [eligible, runChecks, loc.pathname])

  /** Langkah 1 ditandai begitu pakar benar-benar membuka panduannya. Dengan
   *  begini Guide.jsx tidak perlu tahu apa pun soal widget ini. */
  useEffect(() => {
    if (!eligible || guidePatched.current || data.guideRead) return
    if (loc.pathname !== '/admin/panduan') return
    guidePatched.current = true
    api.patch('/me/onboarding', { onboarding_guide_read: true })
      .then(() => setData((d) => ({ ...d, guideRead: true })))
      .catch(() => { guidePatched.current = false })
  }, [eligible, loc.pathname, data.guideRead])

  // `title` boleh berupa fungsi (langkah kurasi berbeda menurut peran), jadi
  // dibereskan di sini supaya JSX di bawah tetap cuma merender teks.
  const states = STEPS.map((s) => ({
    ...s,
    label: t(typeof s.title === 'function' ? s.title(data) : s.title),
    isDone: !!s.done(data),
    text: s.hint(data, t),
  }))
  const doneCount = states.filter((s) => s.isDone).length
  const remaining = STEPS.length - doneCount
  const allDone = remaining === 0 && !loading

  /** Tuntas → buka panel dan tampilkan ucapan selamat.
   *
   *  Widget ini TIDAK menutup dirinya sendiri. Versi pertama menutup otomatis
   *  setelah 3 detik, dan itu keliru: lembaga yang isinya sudah lengkap membuat
   *  enam langkah hijau sejak awal, jadi sekali membuka /admin/panduan seluruh
   *  daftar langsung tuntas dan widgetnya lenyap permanen sebelum sempat dibaca.
   *  Menutup selamanya adalah keputusan sekali jalan — biar pakar sendiri yang
   *  menekannya.
   *
   *  Panelnya dipaksa terbuka: kalau tidak, pakar yang tadi menutup panel cuma
   *  akan melihat badge-nya hilang tanpa penjelasan. */
  useEffect(() => {
    if (!allDone || hidden) return
    setOpen(true)
    setCelebrating(true)
  }, [allDone, hidden])

  const dismiss = () => {
    setHidden(true)
    api.patch('/me/onboarding', { onboarding_hidden: true }).catch(() => {})
  }

  const go = (step) => {
    setOpen(false)
    nav(step.to(data))
  }

  const openPanel = () => {
    setOpen(true)
    if (Date.now() - lastRun.current > 2000) runChecks()
  }

  if (!eligible || hidden) return null

  /* ------------------------------------------------------------------- FAB --- */
  // z-[65]: di atas header admin (z-30), tapi di bawah Modal/Drawer (z-[70])
  // dan Toasts (z-[80]) supaya tidak pernah menghalangi dialog.
  if (!open) {
    return (
      // end-6, bukan right-6: di bahasa Arab seluruh halaman menghadap ke kiri,
      // jadi sudut "tempat menaruh tombol bantuan" pun ikut pindah. Dipaku ke
      // kanan, ia mendarat di sisi yang justru dilewati mata paling akhir.
      <div className="fixed bottom-6 end-6 z-[65] print:hidden">
        <button
          onClick={openPanel}
          title={t('adm.onb.fabTitle')}
          aria-label={t('adm.onb.fabLabel', { n: remaining })}
          className="group relative grid w-14 h-14 place-items-center rounded-full kx-cta-grad text-white shadow-pop transition-all hover:brightness-[1.06] active:translate-y-px"
        >
          <Icon name="rocket_launch" size={24} fill />
          {!loading && remaining > 0 && (
            <span className="absolute -top-1 ltr:-right-1 rtl:-left-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-danger px-1 text-[11px] font-black text-white ring-2 ring-white tabular-nums">
              {remaining}
            </span>
          )}
          {/* Tooltip harus keluar MENJAUHI tepi layar. Di RTL tombolnya ada di
              kiri, jadi tooltipnya keluar ke kanan — kalau tidak, ia terpotong. */}
          <span className="pointer-events-none absolute ltr:right-full ltr:mr-3 rtl:left-full rtl:ml-3 whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-[12px] font-bold text-white opacity-0 shadow-card transition-opacity group-hover:opacity-100">
            {t('adm.onb.fabTip')}
          </span>
        </button>
      </div>
    )
  }

  /* ----------------------------------------------------------------- panel --- */
  return (
    <div className="fixed bottom-6 end-6 z-[65] w-[340px] max-w-[calc(100vw-2rem)] animate-fade-up print:hidden">
      <div className="flex max-h-[70vh] flex-col overflow-hidden rounded-card bg-white shadow-pop border border-line">
        {celebrating ? (
          <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
            <span className="grid w-14 h-14 place-items-center rounded-2xl bg-success/12 text-success">
              <Icon name="rocket_launch" size={28} fill />
            </span>
            <div>
              <p className="font-extrabold">{t('adm.onb.doneTitle')}</p>
              <p className="mt-1 text-[12px] text-muted kx-pretty">
                {t(isPlatform ? 'adm.onb.donePlatform' : 'adm.onb.doneProvider')}{' '}
                {t('adm.onb.doneTail')}{' '}
                <span className="font-bold text-fg">{t('admin.guide')}</span>.
              </p>
            </div>
            {/* Dua pilihan, dan yang menutup PERMANEN bukan yang paling menonjol —
                menutup selamanya tidak bisa dibatalkan dari layar mana pun. */}
            <div className="flex w-full flex-col gap-1.5 pt-1">
              <button
                onClick={dismiss}
                className="h-9 w-full rounded-xl kx-cta-grad text-[13px] font-bold text-white transition hover:brightness-[1.06]"
              >
                {t('adm.onb.doneClose')}
              </button>
              <button
                onClick={() => { setCelebrating(false); setOpen(false) }}
                className="text-[12px] font-bold text-muted transition hover:text-fg"
              >
                {t('adm.onb.doneLater')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
              <span className="grid w-8 h-8 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
                <Icon name="rocket_launch" size={18} fill />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-extrabold leading-tight">{t('adm.onb.panelTitle')}</span>
                <span className="block text-[12px] text-muted">
                  {loading ? t('adm.onb.checking') : t('adm.onb.progress', { n: doneCount, total: STEPS.length })}
                </span>
              </span>
              <IconButton icon="close" label={t('ui.close')} kind="ghost" size={32} onClick={() => setOpen(false)} />
            </div>

            <ProgressBar
              value={(doneCount / STEPS.length) * 100}
              height={4}
              tone={allDone ? 'success' : 'brand'}
              className="rounded-none"
            />

            <ol className="flex-1 overflow-y-auto p-2">
              {states.map((s, i) => (
                <li key={s.key} className="relative">
                  {/* Garis penghubung antar-langkah: inilah yang membuat daftar
                      ini terbaca sebagai stepper, bukan sekadar daftar centang.
                      Angkanya mengikuti geometri bulatan: tombol ber-p-2.5 (10px)
                      + bulatan 22px, jadi pusatnya di x=21px (garis 2px → left 20)
                      dan sisi bawahnya di y=32px. Ujung bawah ditarik melewati
                      batas <li> supaya menyambung ke bulatan berikutnya. */}
                  {i < states.length - 1 && (
                    <span
                      aria-hidden="true"
                      className={`absolute start-[20px] top-[34px] bottom-[-8px] w-[2px] rounded-full ${s.isDone ? 'bg-success/35' : 'bg-line'}`}
                    />
                  )}
                  <button
                    onClick={() => go(s)}
                    className={`group relative flex w-full items-start gap-2.5 rounded-xl p-2.5 text-start transition ${
                      s.isDone ? 'bg-success/[.04] hover:bg-success/[.08]' : 'hover:bg-surface-sub'
                    }`}
                  >
                    <span
                      className={`grid w-[22px] h-[22px] shrink-0 place-items-center rounded-full text-[11px] font-black transition ${
                        loading
                          ? 'bg-black/[.08] text-transparent animate-pulse'
                          : s.isDone
                            ? 'bg-success text-white'
                            : 'bg-white text-muted border-2 border-line'
                      }`}
                    >
                      {s.isDone && !loading ? <Icon name="check" size={14} /> : i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block text-[13.5px] font-extrabold ${
                          s.isDone ? 'text-muted line-through decoration-muted/40' : ''
                        }`}
                      >
                        {s.label}
                      </span>
                      <span className="block text-[12px] text-muted kx-pretty">{s.text}</span>
                    </span>
                    {!s.isDone && (
                      <Icon
                        name="chevron_right"
                        size={18}
                        className={`mt-[2px] shrink-0 text-muted transition-transform group-hover:text-brand ${rtl ? 'group-hover:-translate-x-0.5' : 'group-hover:translate-x-0.5'}`}
                      />
                    )}
                  </button>
                </li>
              ))}
            </ol>

            <div className="flex items-center gap-2 border-t border-line px-4 py-2.5">
              {loading && <Spinner size={13} className="text-muted" />}
              <button
                onClick={dismiss}
                className="ms-auto text-[12px] font-bold text-muted hover:text-danger transition"
              >
                {t('adm.onb.hideForever')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
